import { BirthDateField } from '@/components/form/BirthDateField';
import { InputField } from '@/components/form/InputField';
import { PhoneInputField } from '@/components/form/PhoneInputField';
import { SelectField } from '@/components/form/SelectField';
import { Form } from '@/components/primitives/Form';
import { getCountries } from '@/features/configuration/configurationActions';
import {
  countriesSelector,
  tenantSelector,
} from '@/features/configuration/configurationSelector';
import { updateProfileLoadingSelector } from '@/features/loading/loadingSelectors';
import { buildUpdateProfilePayload } from '@/features/profile/buildUpdateProfilePayload';
import { updateProfile } from '@/features/profile/profileActions';
import { profileDataSelector } from '@/features/profile/profileSelectors';
import { useGenderOptions } from '@/hooks/useGenderOptions';
import {
  useCustomerProfileFieldRenderer,
  type CustomerProfileFieldNames,
} from '@/hooks/usePersonFieldRenderer';
import { useTenantConfiguration } from '@/hooks/useTenantConfiguration';
import { useTranslation } from '@/i18n';
import { TransText } from '@/i18n/trans/text';
import DiscountEligibility from '@/routes/account/accountDetails/DiscountEligibility';
import { useDispatch, useSelector } from '@/store/utils';
import type { ApiErrorResponse } from '@/types/api';
import { cn } from '@/utils/cn';
import { showErrorToast } from '@/utils/errorToast';
import { formatOfficialResidence } from '@/utils/formatOfficialResidence';
import { phonenumberToE164 } from '@/utils/phonenumberToE164';
import {
  createUpdateProfileSchema,
  type UpdateProfileValues,
} from '@/utils/zodSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Divider,
  Icons,
  Input,
  Label,
  Typography,
  typographyVariants,
} from '@packages/webshop-design-system';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FC,
  type ReactNode,
} from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

interface MyInformationProps {}

const MyInformation: FC<MyInformationProps> = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const profile = useSelector(profileDataSelector);
  const countries = useSelector(countriesSelector);
  const isMaas = useSelector(tenantSelector).value === 'maas';
  const isUpdatingProfile = useSelector(updateProfileLoadingSelector);
  const customerProfileFields = useCustomerProfileFieldRenderer();
  const genderOptions = useGenderOptions();
  const tenantConfiguration = useTenantConfiguration();
  const [isLoadingCountries, setIsLoadingCountries] = useState(true);

  const visibleCustomerProfileFields = useMemo(
    () => (isMaas ? [] : customerProfileFields),
    [customerProfileFields, isMaas]
  );

  const officialResidence = useMemo(
    () =>
      formatOfficialResidence({
        streetAddress: profile?.streetAddress,
        city: profile?.city,
        county: profile?.county,
        zipCode: profile?.zipCode,
      }),
    [profile?.city, profile?.county, profile?.streetAddress, profile?.zipCode]
  );

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        await dispatch(getCountries()).unwrap();
      } catch (error) {
        console.error('Failed to fetch countries:', error);
      } finally {
        setIsLoadingCountries(false);
      }
    };

    if (!countries) {
      fetchCountries();
    } else {
      setIsLoadingCountries(false);
    }
  }, [dispatch, countries]);

  const getProfileCountry = useCallback(
    () =>
      profile?.country?.id
        ? countries?.items?.find(({ id }) => id === profile.country?.id)
        : null,
    [countries, profile?.country?.id]
  );

  // Convert profile data to form values. `email` is included for display only:
  // it is part of the profile response but not the update payload, so it is
  // shown as an always-required, read-only field and stripped before submit.
  const defaultValues = useMemo<UpdateProfileValues>(
    () => ({
      email: profile?.email ?? '',
      firstName: profile?.firstName ?? '',
      lastName: profile?.lastName ?? '',
      phoneNumber:
        phonenumberToE164({
          phonenumber: profile?.phoneNumber,
          countryCode:
            getProfileCountry()?.code ?? tenantConfiguration?.defaultCountry,
        }) ??
        profile?.phoneNumber ??
        '',
      // The API returns a date-time (e.g. 1998-11-25T00:00:00), but
      // <input type="date"> only renders a yyyy-MM-dd value.
      birthDate: profile?.birthDate?.split('T')[0] ?? '',
      genderId: profile?.gender?.id ?? '',
      countryId: profile?.country?.id ?? '',
      streetAddress: profile?.streetAddress ?? '',
      city: profile?.city ?? '',
      zipCode: profile?.zipCode ?? '',
      // Not rendered as form fields, but the update is a full replace — read
      // them back off the profile so saving My Information round-trips rather
      // than posting an empty list (BR-57421).
      dietaryRequirementIds:
        profile?.dietaryRequirements?.flatMap(({ id }) => id ?? []) ?? [],
      seatPreferenceIds:
        profile?.seatPreferences?.flatMap(({ id }) => id ?? []) ?? [],
    }),
    [getProfileCountry, profile, tenantConfiguration?.defaultCountry]
  );

  const form = useForm<UpdateProfileValues>({
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    resolver: zodResolver(
      createUpdateProfileSchema(t, visibleCustomerProfileFields)
    ),
    defaultValues,
  });

  const { reset, formState } = form;

  const onSubmit = useCallback(
    async (data: UpdateProfileValues) => {
      try {
        await dispatch(
          updateProfile(buildUpdateProfilePayload(data, profile))
        ).unwrap();
        toast.success(<TransText i18nKey="yourInformationHasBeenUpdated" />);
      } catch (e: unknown) {
        showErrorToast(
          <TransText i18nKey="updatingInformationFailed" />,
          e as ApiErrorResponse
        );
      }
    },
    [dispatch, profile]
  );

  const handleRevert = useCallback(() => {
    reset(defaultValues);
  }, [reset, defaultValues]);

  const countryOptions = useMemo(
    () =>
      countries?.items?.map((country) => ({
        value: country.id ?? '',
        label: country.name ?? '',
      })) ?? [],
    [countries]
  );

  // Renderer per configurable field. `required` is supplied by the caller from
  // the backend `customerProfileDataFields` configuration.
  const fieldRenderers = useMemo<
    Record<CustomerProfileFieldNames, (required: boolean) => ReactNode>
  >(
    () => ({
      firstName: (required) => (
        <InputField
          name="firstName"
          label={<TransText i18nKey="firstName" />}
          required={required}
        />
      ),
      lastName: (required) => (
        <InputField
          name="lastName"
          label={<TransText i18nKey="lastName" />}
          required={required}
        />
      ),
      phoneNumber: (required) => (
        <PhoneInputField
          name="phoneNumber"
          label={<TransText i18nKey="phoneNumber" />}
          required={required}
        />
      ),
      countryId: (required) => (
        <SelectField
          name="countryId"
          label={<TransText i18nKey="nationality" />}
          options={countryOptions}
          required={required}
          placeholder={
            isLoadingCountries ? t('text.loading') : t('text.selectCountry')
          }
          classNames={{ select: 'bg-white' }}
        />
      ),
      birthDate: (required) => (
        <BirthDateField name="birthDate" required={required} />
      ),
      genderId: (required) => (
        <SelectField
          name="genderId"
          label={<TransText i18nKey="gender" />}
          options={genderOptions}
          required={required}
          placeholder={t('text.selectGender')}
          classNames={{ select: 'bg-white' }}
        />
      ),
      streetAddress: (required) => (
        <InputField
          name="streetAddress"
          label={<TransText i18nKey="streetName" />}
          required={required}
        />
      ),
      city: (required) => (
        <InputField
          name="city"
          label={<TransText i18nKey="city" />}
          required={required}
        />
      ),
      zipCode: (required) => (
        <InputField
          name="zipCode"
          label={<TransText i18nKey="zipCode" />}
          required={required}
        />
      ),
    }),
    [countryOptions, genderOptions, isLoadingCountries, t]
  );

  // Split the configured fields into required vs. optional columns, preserving
  // the configuration order and only rendering displayed fields.
  const { requiredFields, optionalFields } = useMemo(() => {
    const required: Array<ReactNode> = [];
    const optional: Array<ReactNode> = [];

    visibleCustomerProfileFields.forEach(({ name, isRequired }) => {
      const node = fieldRenderers[name]?.(isRequired);
      if (!node) {
        return;
      }
      (isRequired ? required : optional).push(
        <div key={name} className="contents">
          {node}
        </div>
      );
    });

    return { requiredFields: required, optionalFields: optional };
  }, [visibleCustomerProfileFields, fieldRenderers]);

  if (isMaas) {
    return (
      <div className="flex h-fit w-full text-dark">
        <div className="w-full rounded-lg bg-white px-6 pb-6 pt-4">
          <Form
            form={form}
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-4"
          >
            <div className="flex items-center gap-3">
              <Icons.user className="h-6 w-6" />
              <Typography variant="heading2">
                <TransText i18nKey="myInformation" />
              </Typography>
            </div>

            <Divider className="mt-4" />

            <div className="flex w-full flex-col gap-4">
              <InputField
                name="email"
                label={<TransText i18nKey="email" />}
                required
                disabled
              />
              <InputField
                name="firstName"
                label={<TransText i18nKey="firstName" />}
                required
                disabled
              />
              <InputField
                name="lastName"
                label={<TransText i18nKey="lastName" />}
                required
                disabled
              />
              <div className="flex flex-col gap-1">
                <Label
                  htmlFor="official-residence"
                  className={cn(
                    typographyVariants({ variant: 'body2' }),
                    'text-neutral'
                  )}
                >
                  <TransText i18nKey="officialResidence" />
                </Label>
                <Input
                  id="official-residence"
                  value={officialResidence}
                  disabled
                  readOnly
                  data-testid="official-residence-input-field"
                />
                <Typography variant="body2" className="text-neutral">
                  <TransText i18nKey="dataFromRahvastikuregister" />
                </Typography>
              </div>
            </div>
          </Form>

          <DiscountEligibility />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-fit w-full text-dark">
      <div className="w-full rounded-lg bg-white px-6 pb-6 pt-4">
        <Form
          form={form}
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-4"
        >
          <div className="flex items-center gap-3">
            <Icons.user className="h-6 w-6" />
            <Typography variant="heading2">
              <TransText i18nKey="myInformation" />
            </Typography>
          </div>

          <Divider className="mt-4" />

          <div className="flex w-full flex-col gap-6 laptop:flex-row laptop:gap-6">
            <div className="flex w-full flex-1 flex-col gap-4">
              <Typography variant="body1-bold">
                <TransText i18nKey="requiredData" />
              </Typography>
              {/* Email is always required and not configurable via
                  customerProfileDataFields; rendered read-only as it is not
                  part of the profile update payload. */}
              <InputField
                name="email"
                label={<TransText i18nKey="email" />}
                required
                disabled
              />
              {requiredFields}
            </div>

            <Divider
              orientation="vertical"
              grow={false}
              className="hidden h-auto laptop:block"
            />
            <Divider className="laptop:hidden" />

            <div className="flex w-full flex-1 flex-col gap-4">
              <Typography variant="body1-bold">
                <TransText i18nKey="optionalData" />
              </Typography>
              {optionalFields}
            </div>
          </div>

          <div className="flex w-full gap-4">
            <Button
              type="button"
              variant="tertiary"
              onClick={handleRevert}
              isDisabled={!formState.isDirty}
              data-testid="revert-changes-button"
            >
              <TransText i18nKey="revertChanges" />
            </Button>
            <Button
              type="submit"
              variant="secondary"
              className="gap-2"
              data-testid="save-profile-button"
              isPending={isUpdatingProfile}
              startAdornment={<Icons.check className="h-5 w-5" />}
            >
              <TransText i18nKey="save" />
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default MyInformation;
